import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useTranslation } from "@/i18n"
import { useAuth } from "@/state/AuthContext"
import { useFeed } from "@/state/FeedContext"
import { useRelays } from "@/state/RelayContext"
import { useNostr } from "@/state/NostrContext"
import { useFollowList } from "@/hooks/useSocial"
import { useTimeline } from "@/hooks/useTimeline"
import { useRelayInfo } from "@/hooks/useRelayData"
import { NoteList } from "@/components/NoteList"
import { FeedSwitcher } from "@/components/FeedSwitcher"
import { RelayCard } from "@/components/RelayCard"
import { AddRelayForm } from "@/components/AddRelayForm"
import { EmptyState, Spinner } from "@/components/Feedback"
import { Icon } from "@/components/Icon"
import { simplifyUrl } from "@/utils/url"

/** Home: the relay-first feed — Following, relay sets and single relay feeds. */
export function FeedPage() {
  const { t } = useTranslation()
  const { pubkey } = useAuth()
  const { feedInfo, feedRelays, isReady, feedName } = useFeed()
  const { favoriteRelays, relaySets, addFavoriteRelays, isReady: libraryReady } = useRelays()
  const { statuses } = useNostr()
  const [switcherOpen, setSwitcherOpen] = useState(false)
  const [relayPanel, setRelayPanel] = useState(false)

  const isFollowing = feedInfo?.feedType === "following"
  const isRelayFeed = feedInfo?.feedType === "relay"
  const following = useFollowList(isFollowing ? pubkey : undefined)
  const { info: relayInfo } = useRelayInfo(isRelayFeed ? feedInfo?.id : undefined)

  const enabled =
    Boolean(feedInfo) && feedRelays.length > 0 && (!isFollowing || following.following.length > 0)
  const timeline = useTimeline({
    relays: feedRelays,
    authors: isFollowing ? following.following : undefined,
    enabled,
  })

  const feedStatus = useMemo(() => {
    const live = statuses.filter((status) => feedRelays.includes(status.url))
    return {
      total: feedRelays.length,
      connected: live.filter((status) => status.state === "connected").length,
      failing: live.filter((status) => status.state === "error" || status.state === "closed"),
    }
  }, [statuses, feedRelays])

  const hasRelays = favoriteRelays.length > 0 || relaySets.length > 0

  if (!libraryReady || !isReady) {
    return <Spinner label={t("Loading relays…")} />
  }

  if (!feedInfo) {
    return (
      <div className="page">
        <EmptyState
          icon="compass"
          title={t("Welcome")}
          description={
            hasRelays
              ? t("Choose a feed to start reading.")
              : t(
                  "This client is built around relays. Add relays you care about, or start from the curated relay collections.",
                )
          }
          action={
            <div className="welcome-actions">
              <Link to="/explore" className="btn btn-primary">
                <Icon name="compass" size={16} />
                {t("Explore Relays")}
              </Link>
              <button type="button" className="btn btn-ghost" onClick={() => setSwitcherOpen(true)}>
                <Icon name="relay" size={16} />
                {t("Switch feed")}
              </button>
            </div>
          }
        />
        <div className="welcome-add">
          <h2 className="section-label">{t("Add your first relay")}</h2>
          <AddRelayForm onAdd={(urls) => addFavoriteRelays(urls)} />
        </div>
        <FeedSwitcher open={switcherOpen} onClose={() => setSwitcherOpen(false)} />
      </div>
    )
  }


  return (
    <div className="page feed-page">
      <header className="page-header">
        <div className="page-header-main">
          <button type="button" className="feed-title-button" onClick={() => setSwitcherOpen(true)}>
            <span className="feed-title">{feedName || t("Feed")}</span>
            <Icon name="chevron-down" size={16} />
          </button>
          <p className="page-subtitle">
            {isFollowing
              ? `${following.following.length} ${t("following")}`
              : `${feedStatus.total} relays · ${feedStatus.connected} ${t("Connected").toLowerCase()}`}
          </p>
        </div>
        <div className="page-header-actions">
          {isRelayFeed && (
            <button
              type="button"
              className={`icon-button ${relayPanel ? "active" : ""}`}
              aria-pressed={relayPanel}
              aria-label={t("Relay details")}
              onClick={() => setRelayPanel((value) => !value)}
            >
              <Icon name="info" size={18} />
            </button>
          )}
          <button type="button" className="icon-button" onClick={timeline.refresh} aria-label={t("Refresh")}>
            <Icon name="refresh" size={18} />
          </button>
        </div>
      </header>

      {isRelayFeed && relayPanel && feedInfo.id && (
        <div className="relay-panel">
          <RelayCard url={feedInfo.id} info={relayInfo} dense />
        </div>
      )}

      {isFollowing && !pubkey && (
        <EmptyState
          icon="users"
          title={t("Log in")}
          description={t("The following feed is built from your contact list, which needs a signed-in session.")}
          action={
            <Link to="/settings" className="btn btn-ghost">
              {t("Settings")}
            </Link>
          }
        />
      )}

      {isFollowing && pubkey && following.following.length === 0 && !following.loading && (
        <EmptyState
          icon="users"
          title={t("You are not following anyone yet")}
          description={t("Open a profile and press Follow, or explore a relay feed to find people.")}
          action={
            <Link to="/explore" className="btn btn-primary">
              {t("Explore")}
            </Link>
          }
        />
      )}

      {feedStatus.failing.length > 0 && (
        <div className="banner banner-warn" role="status">
          <Icon name="alert" size={16} />
          <div>
            <p>
              {feedStatus.failing.length} relay{feedStatus.failing.length === 1 ? " is" : "s are"} unavailable.
              Other relays keep streaming.
            </p>
            <p className="muted small">
              {feedStatus.failing
                .map((status) => `${simplifyUrl(status.url)}: ${status.lastError ?? status.state}`)
                .join(" · ")}
            </p>
          </div>
        </div>
      )}

      {timeline.closeReasons.length > 0 && (
        <div className="banner banner-info" role="status">
          <Icon name="info" size={16} />
          <div>
            <p>Some relays closed the subscription:</p>
            <p className="muted small">{timeline.closeReasons.join(" · ")}</p>
          </div>
        </div>
      )}

      <NoteList
        events={timeline.events}
        loading={timeline.loading || following.loading}
        loadingMore={timeline.loadingMore}
        hasMore={timeline.hasMore}
        onLoadMore={timeline.loadMore}
        emptyTitle={isRelayFeed ? t("No notes from this relay") : t("No notes yet")}
        emptyDescription={
          isRelayFeed
            ? t("The relay may be empty, unreachable, or it may require authentication.")
            : t("This feed has no notes yet. Try another relay set, or check that the relays are reachable.")
        }
        emptyAction={
          isRelayFeed && feedInfo.id ? (
            <Link to={`/relay/${encodeURIComponent(feedInfo.id)}`} className="btn btn-primary">
              <Icon name="relay" size={16} />
              {t("Relay details")}
            </Link>
          ) : (
            <button type="button" className="btn btn-ghost" onClick={() => setSwitcherOpen(true)}>
              <Icon name="relay" size={16} />
              {t("Switch feed")}
            </button>
          )
        }
      />

      <FeedSwitcher open={switcherOpen} onClose={() => setSwitcherOpen(false)} />
    </div>
  )
}
